import { ref, toRaw } from "vue";
import { kebabToCamelCase } from "@bloglovin/vue-framework";
import OptionsMap from "%/dynamic-component-editor/options/options-map";
import OptionContextBinding from "%/dynamic-component-editor/classes/option-context-binding";
import OptionExpression from "@bloglovin/inspire-engine/Helpers/OptionExpression.js";
import BaseBranding from "%/dynamic-component-editor/classes/branding/base-branding";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import PositioningOptions from "%/dynamic-component-editor/bundles/positioning-options";
import { ensurePositioningBundle } from "%/utils/universal-element-options";
import { reportOptionCompileFailure } from "%/dynamic-component-editor/helpers/option-compile-failure-log";

// Single read path and single write path for every element option leaf.
// See docs/CRI_INHERITANCE.md for the storage contract and inheritance graph.
//
// Reads:
//   getOptionValue(elementData, optionKey, breakpoint, state, context)        → { r, c, i }
//   getRawOptionValue(elementData, optionKey, breakpoint, state, context)     → r
//   getCompiledOptionValue(elementData, optionKey, breakpoint, state, context)→ c
//
// All three walk the same inheritance chain. The two convenience wrappers
// just pluck `.r` / `.c` off the result of `getOptionValue`.
//
// Write:
//   processElementOption(elementData, optionKey, breakpoint, state, rawValue, themeMode, context) → { r, c, i }
class DynamicOption {

  /** ====== Theme reactivity ====== */
  // Bumped by ThemeConfigPanel after a draft save. Reading `.value` inside
  // the resolver registers a Vue dep, so a bump re-renders every consumer
  // that read a theme-bound leaf — no per-leaf cache stamp needed.
  static themeCacheVersion = ref(0);

  static bumpThemeCacheVersion() {
    this.themeCacheVersion.value += 1;
  }

  /** ====== Option class registry ====== */
  static #optionClassesByElementType = {};

  /** ====== Public read API ====== */

  // The full {r, c, i} leaf for a coordinate. Walks state / breakpoint
  // inheritance, the branding chain, and OptionClass defaults.
  static getOptionValue(elementData, optionKey, breakpoint, state, context) {
    if (!elementData) {
      return undefined;
    }
    return this.#resolveOptionLeaf(
      elementData, optionKey, this.#normaliseBreakpoint(breakpoint), state, context,
    );
  }

  // Just the raw value at this coordinate.
  static getRawOptionValue(elementData, optionKey, breakpoint, state, context) {
    return this.getOptionValue(elementData, optionKey, breakpoint, state, context)?.r;
  }

  // Just the compiled value at this coordinate. This is what consumers
  // feed into VCL / CSS.
  static getCompiledOptionValue(elementData, optionKey, breakpoint, state, context) {
    return this.getOptionValue(elementData, optionKey, breakpoint, state, context)?.c;
  }

  // Bulk read — one full {r, c, i} leaf per requested optionKey.
  static getOptionValuesBundle(elementData, optionKeys, breakpoint, state, context) {
    const normalisedBreakpoint = this.#normaliseBreakpoint(breakpoint);
    return optionKeys.reduce((bundle, optionKey) => {
      bundle[optionKey] = this.#resolveOptionLeaf(elementData, optionKey, normalisedBreakpoint, state, context);
      return bundle;
    }, {});
  }

  /** ====== Public write API ====== */

  // The only place leaf shape is decided. Every caller (drop-time seeding,
  // inspector edits, paste, programmatic batch-set, theme draft save)
  // routes through here.
  //
  // Output shapes:
  //   { r }              theme-bound override or theme-bound default
  //   { r, c }           non-theme override
  //   { i: true }        branded option, no override (transient — stripped before save)
  //   { r, c, i: true }  OptionClass default materialised at this layer
  //   { r, i: true }     OptionClass default that's a theme reference
  static processElementOption(elementData, optionKey, breakpoint, state, rawValue, themeMode, context) {
    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementData.type, context);

    if (rawValue !== undefined) {
      if (this.#rawIsThemeReference(rawValue)) {
        return { r: rawValue };
      }
      // A Context Binding is a BINDING, not a value: never hand it to the
      // compiler. Written to BOTH r and c because the PHP flattener prefers
      // `c` when present (BrandingChainResolver), so a token in `r` alone
      // would be shadowed by whatever `c` held.
      if (OptionContextBinding.isToken(rawValue)) {
        return { r: rawValue, c: rawValue };
      }
      // Same for an Option Expression: the AST is resolved at render, never
      // compiled. Persist uncompiled into both r and c for the same reason.
      if (OptionExpression.isToken(rawValue)) {
        return { r: rawValue, c: rawValue };
      }
      const compiled = this.#tryCompile(optionClass, rawValue, context.themeId, themeMode, { optionKey, elementId: elementData?.id });
      return compiled !== undefined ? { r: rawValue, c: compiled } : { r: rawValue };
    }

    // No raw value — caller wants the default leaf.
    if (this.#optionInheritsFromBranding(elementData, optionKey)) {
      return { i: true };
    }

    const rawDefault = this.#getRawDefaultValue(breakpoint, state, optionKey, elementData, context);
    if (rawDefault === undefined) {
      return undefined;
    }
    if (this.#rawIsThemeReference(rawDefault)) {
      return { r: rawDefault, i: true };
    }
    const compiled = this.#tryCompile(optionClass, rawDefault, context.themeId, themeMode, { optionKey, elementId: elementData?.id });
    return compiled !== undefined
      ? { r: rawDefault, c: compiled, i: true }
      : { r: rawDefault, i: true };
  }

  // Bulk write — produces the full options tree for an element by walking
  // every (mode × breakpoint × state × optionKey) coord and asking
  // processElementOption for each one. Used by drop-time seeding.
  static processElementOptions(elementDef, elementData, rootOptions, themeMode = "light-mode", context) {
    const tree = {};
    const allOptionClasses = this.getAllOptionClassesForElement(elementData.type, context);
    const breakpoints = ["default", ...context.breakpoints.map(breakpoint => breakpoint.alias)];
    const states = elementDef.availableStates;

    const variableOverrides = this.#getApplicableVariableOverwrites(
      elementData.elementTemplateId,
      rootOptions["option-variables"] || {},
      rootOptions["linked-option-variables"] || {},
    );

    allOptionClasses.forEach(optionClass => {
      const optionKey = optionClass.optionKey;
      breakpoints.forEach(breakpoint => {
        tree[breakpoint] = tree[breakpoint] || {};
        states.forEach(state => {
          tree[breakpoint][state] = tree[breakpoint][state] || {};
          const leaf = this.#processSeedLeaf(
            optionKey, breakpoint, state, elementData, variableOverrides, themeMode, context,
          );
          if (leaf) {
            tree[breakpoint][state][optionKey] = leaf;
          }
        });
      });
    });

    return tree;
  }

  /** ====== Tree persistence helpers ====== */

  // Parse the persisted `options` JSON. Drops stale theme caches and clears
  // any non-`r` snapshot from `{i: true}` leaves so the resolver re-walks
  // on next read.
  static parseOptions(raw) {
    if (raw === null || raw === undefined || raw === "") {
      return {};
    }
    let parsed;
    if (typeof raw === "object") {
      parsed = toRaw(raw);
    } else if (typeof raw === "string") {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return {};
      }
    } else {
      return {};
    }
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return this.#cleanPersistedTree(parsed);
  }

  // Immutable write into {mode:{breakpoint:{state:{optionKey:leaf}}}}.
  // Returns a new tree with every touched level cloned for Vue reactivity.
  static setOptionValue(tree, themeMode, breakpoint, state, optionKey, value) {
    const nextTree = { ...tree || {} };
    const modeBucket = { ...nextTree[themeMode] || {} };
    const breakpointBucket = { ...modeBucket[breakpoint] || {} };
    const stateBucket = { ...breakpointBucket[state] || {} };
    stateBucket[optionKey] = value;
    breakpointBucket[state] = stateBucket;
    modeBucket[breakpoint] = breakpointBucket;
    nextTree[themeMode] = modeBucket;
    return nextTree;
  }

  // Plain path read; returns undefined when any segment of the path is
  // missing.
  static getOptionValueFromTree(tree, themeMode, breakpoint, state, optionKey) {
    return tree?.[themeMode]?.[breakpoint]?.[state]?.[optionKey];
  }

  // Pre-save scrubber. Drops bare `{i: true}` leaves (absence of leaf is
  // the storage form for "no override") and prunes empty branches.
  static stripUnpersistableValues(tree) {
    const next = {};
    for (const mode of Object.keys(tree || {})) {
      const cleanedMode = this.#cleanModeBranch(tree[mode]);
      if (Object.keys(cleanedMode).length > 0) {
        next[mode] = cleanedMode;
      }
    }
    return next;
  }

  // Production blob shape: keep only `{c}` per leaf (plus the `i: true`
  // inheritance marker when present), drop empty branches.
  //
  // `i: true` is preserved so the production resolver can still tell
  // "this leaf's `c` came from the brand chain at the time of publish,
  // re-walk the chain when reading at a different (breakpoint, state)"
  // apart from "this leaf is a hard user override, use it everywhere
  // unless explicitly overridden". Without that distinction, the
  // production resolver in `#resolveLeafForCustomBreakpoint` treats the
  // pre-compiled default-bp value as authoritative and stops walking,
  // so per-breakpoint flips of brand-determining keys (`text-style-
  // option`, `button-type`, …) never re-route the chain at the active
  // breakpoint and downstream colors / sizes stay pinned to the
  // default-bp resolution. See `DynamicOption.#resolveOptionLeaf`'s
  // branding fall-through for the live read path that depends on this.
  static minimize(tree) {
    const minimized = {};
    for (const themeMode in tree) {
      for (const breakpoint in tree[themeMode]) {
        for (const state in tree[themeMode][breakpoint]) {
          for (const optionKey in tree[themeMode][breakpoint][state]) {
            const leaf = tree[themeMode][breakpoint][state][optionKey];
            if (leaf?.c === undefined) {
              continue;
            }
            minimized[themeMode] = minimized[themeMode] || {};
            minimized[themeMode][breakpoint] = minimized[themeMode][breakpoint] || {};
            minimized[themeMode][breakpoint][state] = minimized[themeMode][breakpoint][state] || {};
            const minimizedLeaf = { c: leaf.c };
            if (leaf.i === true) {
              minimizedLeaf.i = true;
            }
            minimized[themeMode][breakpoint][state][optionKey] = minimizedLeaf;
          }
        }
      }
    }
    return minimized;
  }

  // Walk a persisted tree and collect "<state>:<breakpoint>:<optionKey>"
  // keys for every leaf that's a real user override (`r` defined, no
  // `i: true`).
  //
  // Canvas elements skip default/default — every dropped element seeds
  // those slots from branding / OptionClass defaults, and those carry
  // routing keys (button-type, button-size, …) without an `i` marker.
  // Theme elements skip nothing: branding is breakpoint-agnostic and
  // their default/default IS where overrides land.
  static collectOverriddenOptionKeys(tree, elementType = null) {
    const overriddenKeys = new Set();
    if (!tree || typeof tree !== "object") {
      return [];
    }
    const isThemeElement = elementType === "theme";
    for (const mode of Object.keys(tree)) {
      for (const breakpoint of Object.keys(tree[mode] || {})) {
        for (const state of Object.keys(tree[mode][breakpoint] || {})) {
          if (!isThemeElement && state === "default" && breakpoint === "default") {
            continue;
          }
          const optionsAtCoord = tree[mode][breakpoint][state];
          for (const optionKey of Object.keys(optionsAtCoord || {})) {
            const leaf = optionsAtCoord[optionKey];
            if (leaf?.r !== undefined && !leaf.i) {
              overriddenKeys.add(`${state}:${breakpoint}:${optionKey}`);
            }
          }
        }
      }
    }
    return Array.from(overriddenKeys);
  }

  /** ====== Default helpers ====== */

  // Build a `{r, c, i: true}` leaf from an OptionClass's own default.
  // Returns undefined when the class exposes no default — callers can then
  // distinguish "no value" from "value is falsy".
  static buildClassDefaultValue(optionClass, themeId, themeMode) {
    if (!optionClass) {
      return undefined;
    }
    const defaults = optionClass.getValueOrDefault({});
    const rawDefault = defaults[`default.${optionClass.optionKey}`];
    if (rawDefault === undefined) {
      return undefined;
    }
    const compiled = this.#tryCompile(optionClass, rawDefault, themeId, themeMode);
    return { r: rawDefault, c: compiled, i: true };
  }

  // Inspector seed wrapper: read at the requested coord, fall back through
  // the resolver, finally cache an OptionClass default into default/default
  // so subsequent saves persist it. Returns { value, tree, cached } so the
  // caller can adopt the new tree when a default was cached.
  static resolveOptionValueWithCache({
    tree, themeMode, breakpoint, state, optionKey, optionClass, themeId, inheritanceResolver,
  }) {
    const direct = this.getOptionValueFromTree(tree, themeMode, breakpoint, state, optionKey);
    if (direct !== undefined && (direct.r !== undefined || direct.c !== undefined)) {
      if (direct.i === true && inheritanceResolver) {
        const resolved = inheritanceResolver(tree, themeMode, breakpoint, state, optionKey);
        if (resolved !== undefined && (resolved.r !== undefined || resolved.c !== undefined)) {
          return { value: resolved, tree, cached: false };
        }
      }
      return { value: direct, tree, cached: false };
    }

    const inherited = inheritanceResolver
      ? inheritanceResolver(tree, themeMode, breakpoint, state, optionKey)
      : undefined;
    if (inherited !== undefined && (inherited.r !== undefined || inherited.c !== undefined)) {
      return { value: inherited, tree, cached: false };
    }

    const classDefault = this.buildClassDefaultValue(optionClass, themeId, themeMode);
    if (classDefault === undefined) {
      return { value: undefined, tree, cached: false };
    }
    const nextTree = this.setOptionValue(tree, themeMode, "default", "default", optionKey, classDefault);
    return { value: classDefault, tree: nextTree, cached: true };
  }

  /** ====== Inspector helpers ====== */

  static getAllOverwrittenOptionKeys(elementData, breakpoint, state, context) {
    const normalisedBreakpoint = this.#normaliseBreakpoint(breakpoint);
    const optionKeys = Object.keys(elementData.options?.[context.themeMode]?.[normalisedBreakpoint]?.[state] || {});
    const overridden = [];

    optionKeys.forEach(optionKey => {
      const leaf = this.#readLeafFromTree(elementData, normalisedBreakpoint, state, optionKey, context.themeMode);
      if (!leaf || leaf.r === undefined || leaf.i) {
        return;
      }
      // At default state on a non-custom breakpoint, only count the leaf
      // as overriding when it overrides something — i.e. there's a
      // branding value behind it. Otherwise it's a routing key
      // (button-type, …) seeded at drop time, not a user edit.
      if (state === "default") {
        if (this.#getRawBrandingValue(normalisedBreakpoint, state, optionKey, elementData, context)) {
          overridden.push(optionKey);
        }
        return;
      }
      overridden.push(optionKey);
    });

    return overridden;
  }

  static determineIfOptionsAreOverwritten(elementData, optionKeys, breakpoint, state, context) {
    if (!elementData) {
      return false;
    }
    const normalisedBreakpoint = this.#normaliseBreakpoint(breakpoint);

    if (!this.#isCustomState(state) && !this.#isCustomBreakpoint(normalisedBreakpoint)) {
      if (elementData.elementType === "theme"
        && BaseBranding.isBaseThemeElement(elementData.brandedVariant, elementData.brandedSize)) {
        return false;
      }
    }

    return optionKeys.some(optionKey => {
      const leaf = this.#readLeafFromTree(elementData, normalisedBreakpoint, state, optionKey, context.themeMode);
      if (!leaf || leaf.r === undefined || leaf.i) {
        return false;
      }
      if (!this.#isCustomState(state) && !this.#isCustomBreakpoint(normalisedBreakpoint)) {
        if (elementData.elementType === "theme") {
          return true;
        }
        return !!this.#getRawBrandingValue(normalisedBreakpoint, state, optionKey, elementData, context);
      }
      return true;
    });
  }

  static determineIfOptionsAreInheriting(elementData, optionKeys, breakpoint, state, context) {
    if (!elementData) {
      return false;
    }
    const normalisedBreakpoint = this.#normaliseBreakpoint(breakpoint);

    return optionKeys.some(optionKey => {
      const leaf = this.#readLeafFromTree(elementData, normalisedBreakpoint, state, optionKey, context.themeMode);
      const looksInherited = leaf === undefined || leaf.i === true;

      if (elementData.elementType === "theme"
        && BaseBranding.isInspectorBaseThemeElement(elementData.brandedVariant, elementData.brandedSize)) {
        return false;
      }

      if (looksInherited && !this.#isCustomState(state) && !this.#isCustomBreakpoint(normalisedBreakpoint)) {
        return !!this.#getRawBrandingValue(normalisedBreakpoint, state, optionKey, elementData, context);
      }
      return looksInherited;
    });
  }

  // Public wrapper around the private branding-chain walker — exposed so
  // `DynamicElement.hasBrandingFallback` can memoise the result. Returns
  // the raw branding value (or undefined) without merging with the
  // element's own option tree, mirroring what the internal callers use
  // for the inheriting-state check.
  static computeBrandingFallback(elementData, optionKey, breakpoint, state, context) {
    if (!elementData) {
      return undefined;
    }
    const normalised = this.#normaliseBreakpoint(breakpoint);
    return this.#getRawBrandingValue(normalised, state, optionKey, elementData, context);
  }

  static optionCanBeInherited(elementData, optionKey, breakpoint, state, context) {
    const normalisedBreakpoint = this.#normaliseBreakpoint(breakpoint);
    if (this.#isCustomState(state) || this.#isCustomBreakpoint(normalisedBreakpoint)) {
      return true;
    }
    if (elementData.elementType === "theme"
      && BaseBranding.isInspectorBaseThemeElement(elementData.brandedVariant, elementData.brandedSize)) {
      return false;
    }
    return !!this.#getRawBrandingValue(normalisedBreakpoint, state, optionKey, elementData, context);
  }

  /** ====== Option class registry ====== */

  static getVariableClasses(rootOptionsVariables) {
    const optionsMap = OptionsMap.optionsMap;
    const variables = rootOptionsVariables || {};
    const classes = [];

    Object.keys(variables).forEach(key => {
      const optionKey = key.split(".").at(-1);
      const camelKey = kebabToCamelCase(optionKey);
      const Klass = optionsMap[camelKey];
      if (Klass) {
        const variable = variables[key];
        const instance = new Klass({ default: variable.value });
        instance.overrideLabel = variable.label;
        instance.optionKey = key;
        classes.push(instance);
      }
    });
    return classes;
  }

  static getElementOptionClasses(elementDef) {
    const classes = [];
    for (const Entry of elementDef?.options || []) {
      classes.push(typeof Entry === "function" ? new Entry() : Entry);
    }
    return classes;
  }

  static getElementOptionBundles(elementDef) {
    const bundles = [];
    for (const BundleClass of elementDef?.optionBundles || []) {
      bundles.push(typeof BundleClass === "function" ? new BundleClass() : BundleClass);
    }
    return ensurePositioningBundle(bundles, PositioningOptions);
  }

  // eslint-disable-next-line no-unused-vars
  static getAllOptionClassesForElement(elementType, context) {
    if (this.#optionClassesByElementType[elementType]) {
      return this.#optionClassesByElementType[elementType];
    }
    const elementDef = DynamicElement.getElementDefinition(elementType);
    if (!elementDef) {
      // Definition not registered yet. Don't cache the empty result —
      // the registry may populate later (DC's `availableElementVueComponents`
      // hydrates async), and a cached empty list would stick.
      return [];
    }
    const classes = this.getElementOptionClasses(elementDef);
    for (const bundle of this.getElementOptionBundles(elementDef)) {
      for (const Entry of bundle.options) {
        classes.push(typeof Entry === "function" ? new Entry() : Entry);
      }
    }
    this.#optionClassesByElementType[elementType] = classes;
    return classes;
  }

  /** ====== Private — the resolver ====== */

  // The single resolver behind every read. Always returns a {r, c, i}
  // leaf or undefined (the latter only when the OptionClass itself
  // exposes no default for this option).
  static #resolveOptionLeaf(elementData, optionKey, breakpoint, state, context) {
    // Touch the reactive ref so consumers re-render when the theme bumps.
    // The actual lookup against the live theme happens in #recompileLeaf
    // below.
    void this.themeCacheVersion.value;

    const leaf = this.#readLeafFromTree(elementData, breakpoint, state, optionKey, context.themeMode);

    if (this.#leafIsRealOverride(leaf, elementData)) {
      // Recompile when:
      //  - the leaf is theme-bound (live theme reactivity — `c` may be
      //    stale against the current theme draft).
      //  - the leaf has `r` but no `c` (write-time compile failed or
      //    the leaf was persisted by a path that didn't compile, e.g.
      //    palette refs written when the theme palette wasn't
      //    registered yet — symptom: bg-color stored as
      //    `{r:{source:"palette.background",value:"red.600"}}` with
      //    no `c`, painting nothing on the canvas / preview).
      // Otherwise trust the cached `c`.
      if (this.#leafIsThemeBound(leaf) || leaf.c === undefined) {
        return this.#recompileLeaf(leaf, optionKey, elementData, context);
      }
      return this.#withoutStaleIdentityCache(leaf, optionKey, elementData, context);
    }

    // `{i: true}` snapshot. For branded elements the chain is the source
    // of truth and we re-walk on every read; for non-branded elements
    // there's no chain, so the OptionClass default IS the value.
    //
    // NOTE: `parseOptions`' `#cleanPersistedTree` strips `r` and `c`
    // from `i:true` leaves on every parse so stale snapshots can't
    // poison the resolver. That means a non-branded `{i:true}` leaf
    // arriving here is BARE (no `r`, no `c`) — returning it as-is
    // sends `undefined` to the consumer (e.g. `direction()` reads
    // `getCompiledOptionValue → leaf.c` and gets undefined, then
    // Container.vue's template falls through to iRow). Rebuild the
    // OptionClass default leaf instead so non-branded reads get a
    // fully-populated `{r, c, i:true}` back.
    if (leaf?.i === true) {
      if (!this.#elementHasBrandingProvider(elementData) && elementData?.elementType !== "theme") {
        if (this.#leafIsThemeBound(leaf)) {
          return this.#recompileLeaf(leaf, optionKey, elementData, context);
        }
        if (leaf.r !== undefined || leaf.c !== undefined) {
          return leaf;
        }
        return this.#buildOptionClassDefaultLeaf(elementData, optionKey, context) ?? leaf;
      }
      if (this.#optionIsBrandingDeterminingKey(optionKey)) {
        // Branding-determining key cannot enter the chain — that's the
        // probe's input. Treat the snapshot as authoritative; if no real
        // override exists at any coord the resolver lands on the
        // OptionClass default below.
        return this.#leafIsThemeBound(leaf)
          ? this.#recompileLeaf(leaf, optionKey, elementData, context)
          : leaf;
      }
      const branded = this.#resolveLeafFromBranding(elementData, optionKey, context, breakpoint, state);
      if (branded !== undefined) {
        return this.#leafIsThemeBound(branded)
          ? this.#recompileLeaf(branded, optionKey, elementData, context)
          : branded;
      }
      return this.#buildOptionClassDefaultLeaf(elementData, optionKey, context);
    }

    // No usable leaf at this coord. Walk inheritance.
    if (this.#isCustomBreakpoint(breakpoint)) {
      const inherited = this.#resolveLeafForCustomBreakpoint(elementData, optionKey, breakpoint, state, context);
      if (inherited !== undefined) {
        return inherited;
      }
    }

    if (this.#isCustomState(state)) {
      // Hover / focus / etc. with no override at this coord. Two
      // distinct cases drive what should win:
      //
      // 1. Element has a HARD user override at (bp, default) — a leaf
      //    with `c` (and `r`) but no `i: true`. The user customized
      //    this option on the element instance; hovering should keep
      //    that customization (same bg as default) UNLESS the user
      //    also sets an explicit hover override. Falling through to
      //    the brand chain's hover here would silently revert the
      //    user's per-element override on hover — visible symptom: a
      //    button whose default bg was overridden flips to the brand
      //    source's hover bg the moment the cursor enters it.
      //
      // 2. Element is inheriting at (bp, default) — leaf is missing,
      //    or marked `i: true`. There IS no per-element customization
      //    to preserve, so the brand chain gets a chance at this
      //    state. A hover override on the brand source (e.g. Button /
      //    Primary / Standard) should beat the brand's inherited
      //    default state on its sibling rows (Primary / Small /
      //    Large) — that's how the brand author signals "all primary
      //    buttons darken on hover".
      const leafAtDefault = this.#readLeafFromTree(elementData, breakpoint, "default", optionKey, context.themeMode);
      if (this.#leafIsRealOverride(leafAtDefault, elementData)) {
        return this.#resolveOptionLeaf(elementData, optionKey, breakpoint, "default", context);
      }
      if (
        (this.#elementHasBrandingProvider(elementData) || elementData?.elementType === "theme")
        && !this.#optionIsBrandingDeterminingKey(optionKey)
      ) {
        const brandedAtState = this.#resolveLeafFromBranding(elementData, optionKey, context, breakpoint, state);
        if (brandedAtState !== undefined) {
          return this.#leafIsThemeBound(brandedAtState)
            ? this.#recompileLeaf(brandedAtState, optionKey, elementData, context)
            : brandedAtState;
        }
      }
      // Chain had nothing at this state either → fall through to the
      // default state on the same breakpoint, which re-runs the full
      // pipeline (and walks the chain again at default).
      return this.#resolveOptionLeaf(elementData, optionKey, breakpoint, "default", context);
    }

    // Default coord with no leaf. Theme elements still need the chain
    // (template configs only populate base coords like primary.standard;
    // primary.small inherits up). Branding-determining keys skip the
    // chain — the chain READS them, so resolving them through the chain
    // would recurse.
    if (
      (this.#elementHasBrandingProvider(elementData) || elementData?.elementType === "theme")
      && !this.#optionIsBrandingDeterminingKey(optionKey)
    ) {
      const branded = this.#resolveLeafFromBranding(elementData, optionKey, context, breakpoint, state);
      if (branded !== undefined) {
        return this.#leafIsThemeBound(branded)
          ? this.#recompileLeaf(branded, optionKey, elementData, context)
          : branded;
      }
    }

    return this.#buildOptionClassDefaultLeaf(elementData, optionKey, context);
  }

  static #staleIdentityCachesReported = new Set();

  // An identity-compiled option's `c` must equal its `r`; a seeded copy left foreign component ids
  // in `c` (task 226), so the canvas rendered a component the inspector never showed.
  static #withoutStaleIdentityCache(leaf, optionKey, elementData, context) {
    if (leaf.r === undefined || String(leaf.c) === String(leaf.r)) {
      return leaf;
    }
    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementData?.type, context);
    const Klass = typeof optionClass === "function" ? optionClass : optionClass?.constructor;
    if (!Klass?.compiledIsRaw) {
      return leaf;
    }
    const reportKey = `${elementData?.id}:${optionKey}`;
    if (!this.#staleIdentityCachesReported.has(reportKey)) {
      this.#staleIdentityCachesReported.add(reportKey);
      const destination = elementData?.destinationId ? `, destination ${elementData.destinationId}` : "";
      // eslint-disable-next-line no-console
      console.error(
        `[DynamicOption] element ${elementData?.id} (dynamic component ${elementData?.dynamicComponentId}${destination}) `
        + `caches ${optionKey} = ${leaf.c} but its author chose ${leaf.r}; rendering ${leaf.r}. The stored row is stale — `
        + "php scripts/report_component_references.php --all --repair-sql prints its repair.",
      );
    }
    return { ...leaf, c: leaf.r };
  }

  // Direct tree lookup. No inheritance, no defaulting.
  static #readLeafFromTree(elementData, breakpoint, state, optionKey, themeMode) {
    return elementData?.options?.[themeMode]?.[breakpoint]?.[state]?.[optionKey];
  }

  // True when the leaf carries an authoritative value at this layer:
  // either an `r`-defined override (canvas / staging tree), or a
  // `c`-only leaf (production release blob — `minimize()` strips
  // everything but `c`). Excluded: bare `{i: true}` snapshots and
  // SpacingPicker all-sides-null partial resets.
  //
  // The all-sides-null exclusion is intentional for branded elements:
  // the SpacingPicker's "remove" affordance writes `{top:null,
  // right:null, bottom:null, left:null}` so the resolver walks the
  // brand chain instead of pinning the leaf to "0". But for elements
  // with no branding provider (root containers, plain layouts), the
  // walk just falls through to the OptionClass static default —
  // `Padding.defaultValue = {top:"standard", …}` compiles to 12px,
  // not the "0" the user expected. Honour the persisted `c` (which
  // the picker already compiled to all-0px) when no branding chain
  // is available so "remove override" actually shows no padding.
  static #leafIsRealOverride(leaf, elementData) {
    if (!leaf || leaf.i === true) {
      return false;
    }
    if (leaf.r === undefined && leaf.c === undefined) {
      return false;
    }
    if (leaf.r !== undefined && this.#leafIsAllSidesNull(leaf)) {
      if (this.#elementHasBrandingProvider(elementData) || elementData?.elementType === "theme") {
        return false;
      }
    }
    return true;
  }

  // SpacingPicker partial-reset artifact: every side of the raw is null.
  // Treat as "no override" so the resolver walks branding.
  static #leafIsAllSidesNull(leaf) {
    const raw = leaf?.r;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return false;
    }
    const keys = Object.keys(raw);
    if (keys.length === 0) {
      return false;
    }
    return keys.every(key => raw[key] === null);
  }

  static #leafIsThemeBound(leaf) {
    return this.#valueIsThemeBound(leaf?.r, 0);
  }

  // Composite options nest their theme ref below the top level (border-sides:
  // `r.top.color.source`) — a flat `r.source` check would miss it and never recompile.
  static #valueIsThemeBound(value, depth) {
    const isPlainObject = value !== null && typeof value === "object";
    const tooDeepToMatter = depth > 2;
    if (!isPlainObject || tooDeepToMatter) {
      return false;
    }
    const source = value.source;
    const hasStringSource = typeof source === "string";
    // "theme."/"semantic."/"palette." refs all live in the theme store and go stale the same way.
    const isThemeSourcePrefix = hasStringSource
      && (source.startsWith("theme.") || source.startsWith("semantic.") || source.startsWith("palette."));
    const isThemeBound = isThemeSourcePrefix || value.context === "theme";
    if (isThemeBound) {
      return true;
    }
    return Object.values(value).some(child => this.#valueIsThemeBound(child, depth + 1));
  }

  // Compute a fresh `c` for a leaf that needs one. Used for:
  //   - Theme-bound leaves (live token reactivity).
  //   - Any leaf where `c` is missing (write-time compile failed or
  //     legacy data was persisted without it).
  //
  // Tries two paths and uses whichever produces a value:
  //   1. The OptionClass's own `convertConfigValueToCompiledValue` —
  //      the canonical write-time compile. Knows how to resolve
  //      spacing tokens, palette colors against the live style store,
  //      etc.
  //   2. The Theme's `transformIntoThemeValues` — handles theme /
  //      semantic / palette source tokens via the theme's flattened
  //      config map.
  //
  // Falls back to the original leaf when neither produces a value, so
  // downstream style validators don't see `null` for `c` after a
  // failed lookup.
  static #recompileLeaf(leaf, optionKey, elementData, context) {
    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementData?.type, context);
    const where = { optionKey, elementId: elementData?.id };
    const compiledViaOptionClass = this.#tryCompile(optionClass, leaf.r, context?.themeId, context?.themeMode, where);
    // Several option classes (FontSize, FontColor, …) implement
    // `convertConfigValueToCompiledValue` as: "look up the value in
    // themeStructure; if missing, return the raw key string". For
    // theme refs that don't exist in the destination's theme — common
    // after a paste from a publication using deprecated keys like
    // `theme.font.verySmall` or `theme.font.displayMedium` — that
    // returns the bare key as a "compiled" value. VCL's style
    // validator then rejects it (`StyleValue is not valid for size.font
    // value verySmall`). Treat such a passthrough as compile failure
    // so the theme-transform path and the OptionClass-default fallback
    // get a chance to produce a valid value.
    if (compiledViaOptionClass !== undefined && !this.#compileIsThemeRefPassthrough(leaf.r, compiledViaOptionClass)) {
      return { ...leaf, c: compiledViaOptionClass };
    }
    const transform = context?.theme?.transformIntoThemeValues;
    if (typeof transform !== "function") {
      // No theme transform available. Fall through to the OptionClass-
      // default fallback below rather than returning the bare `r`-only
      // leaf — VCL's style validator rejects undefined `c`.
      const classDefault = this.#buildOptionClassDefaultLeaf(elementData, optionKey, context);
      return classDefault ?? leaf;
    }
    let compiledViaTheme = null;
    try {
      compiledViaTheme = transform.call(
        context.theme,
        this.#parseThemeSourceForOption(leaf.r, optionKey),
        context.themeMode,
      );
    } catch {
      compiledViaTheme = null;
    }
    if (compiledViaTheme === null || compiledViaTheme === undefined) {
      // Last-resort fallback: the theme reference doesn't resolve in
      // this destination's theme. Common after a paste from a
      // publication that used different theme key names (e.g. legacy
      // `theme.font.verySmall` doesn't exist in a destination whose
      // theme renamed it to `theme.font.extraSmall`). Returning the
      // OptionClass's static default produces a renderable `c` value
      // so VCL's style validator doesn't trip on undefined; the user
      // can re-select a destination-theme key in the inspector if the
      // default isn't what they want.
      const classDefault = this.#buildOptionClassDefaultLeaf(elementData, optionKey, context);
      if (classDefault !== undefined) {
        return classDefault;
      }
      return leaf;
    }
    return { ...leaf, c: compiledViaTheme };
  }

  // The theme source key needs a trailing `.color` / `.size` / `.font`
  // suffix when the option-class consumes a sub-property of the token.
  // Mirrors the legacy mapping; future option-classes can add their key
  // to the relevant bucket.
  static #parseThemeSourceForOption(rawValue, optionKey) {
    if (!rawValue?.source?.startsWith("theme.")) {
      return rawValue;
    }
    if (["font-color", "icon-color", "count-color", "background-color", "border-color"].includes(optionKey)) {
      return { ...rawValue, source: rawValue.source + ".color" };
    }
    if (["font-size", "count-size", "border-width"].includes(optionKey)
      || rawValue.source.includes(".spacing")) {
      return { ...rawValue, source: rawValue.source + ".size" };
    }
    if (rawValue.source.includes("weight")) {
      return { ...rawValue, source: rawValue.source + ".font" };
    }
    return rawValue;
  }

  // Custom-state inheritance is folded into the main resolver via the
  // recursive call in #resolveOptionLeaf. This helper is only the
  // breakpoint walker — for a custom breakpoint:
  //   1. fall back to the default state at the SAME breakpoint
  //   2. walk broader breakpoints (mobile-first walks down,
  //      desktop-first walks up) at this state, then their default state
  //   3. fall back to the default breakpoint at the SAME state
  //   4. fall back to the default breakpoint's default state
  static #resolveLeafForCustomBreakpoint(elementData, optionKey, breakpoint, state, context) {
    // Helper — marks an inherited leaf with `i: true` (so the consumer
    // can tell this leaf is being pulled from elsewhere) and re-runs
    // the theme compile when the leaf is theme-bound. The main resolver
    // path (`#resolveOptionLeaf` line 504-519) already does the
    // recompile for direct hits; the custom-breakpoint inheritance
    // path previously skipped it, so a theme-bound override at
    // (default-bp, default-state) returned to a custom-breakpoint read
    // came back with `c: undefined` (theme refs are stored as `{r:…}`
    // with no `c`, and `c` is computed lazily at read time). The
    // visible symptom: a container with bg="theme.background.paper"
    // set on the default canvas showed transparent on every other
    // breakpoint, and clicking the inspector's "override" plus icon
    // (which writes the same value to that breakpoint's slot) made it
    // appear because the write path produces a fresh leaf the resolver
    // then handles via the direct-hit branch.
    // `from` names the breakpoint the value came from (task 206 visibility callouts).
    const inherit = (leaf, from) => {
      const inherited = { ...leaf, i: true, from };
      if (this.#leafIsThemeBound(inherited)) {
        return this.#recompileLeaf(inherited, optionKey, elementData, context);
      }
      if (inherited.c === undefined && inherited.r !== undefined) {
        return this.#recompileLeaf(inherited, optionKey, elementData, context);
      }
      return inherited;
    };

    const defaultStateAtSameBreakpoint = this.#readLeafFromTree(elementData, breakpoint, "default", optionKey, context.themeMode);
    if (this.#leafIsRealOverride(defaultStateAtSameBreakpoint, elementData)) {
      return inherit(defaultStateAtSameBreakpoint, breakpoint);
    }

    const inheritedFromBroaderBreakpoint = this.#findInheritedBreakpointValue(state, optionKey, breakpoint, elementData, context);
    if (inheritedFromBroaderBreakpoint !== undefined) {
      return inherit(
        { r: inheritedFromBroaderBreakpoint.value, c: inheritedFromBroaderBreakpoint.compiled },
        inheritedFromBroaderBreakpoint.from,
      );
    }

    const sameStateAtDefaultBreakpoint = this.#readLeafFromTree(elementData, "default", state, optionKey, context.themeMode);
    if (this.#leafIsRealOverride(sameStateAtDefaultBreakpoint, elementData)) {
      return inherit(sameStateAtDefaultBreakpoint, "default");
    }

    const defaultCoordAtDefaultBreakpoint = this.#readLeafFromTree(elementData, "default", "default", optionKey, context.themeMode);
    if (this.#leafIsRealOverride(defaultCoordAtDefaultBreakpoint, elementData)) {
      return inherit(defaultCoordAtDefaultBreakpoint, "default");
    }

    return undefined;
  }

  static #findInheritedBreakpointValue(state, optionKey, currentBreakpointAlias, elementData, context) {
    // `context.breakpoints` is required by every walk site below, but
    // older callers (production-blob render path, resolver smoke
    // tests) sometimes built a context without it. Treat absence as
    // an empty list — the walk yields no inherited value and the
    // caller falls through to its defaults — instead of crashing
    // the entire render with a TypeError.
    const breakpoints = Array.isArray(context?.breakpoints) ? context.breakpoints : [];
    const currentBreakpoint = breakpoints.find(breakpoint => breakpoint.alias === currentBreakpointAlias);
    if (!currentBreakpoint) {
      return undefined;
    }
    for (const broaderBreakpoint of this.#getBroaderBreakpoints(currentBreakpoint, breakpoints)) {
      const valueAtState = this.#readLeafFromTree(elementData, broaderBreakpoint.alias, state, optionKey, context.themeMode);
      if (this.#leafIsRealOverride(valueAtState, elementData)) {
        return { value: valueAtState.r, compiled: valueAtState.c, from: broaderBreakpoint.alias };
      }
      const valueAtDefault = this.#readLeafFromTree(elementData, broaderBreakpoint.alias, "default", optionKey, context.themeMode);
      if (this.#leafIsRealOverride(valueAtDefault, elementData)) {
        return { value: valueAtDefault.r, compiled: valueAtDefault.c, from: broaderBreakpoint.alias };
      }
    }
    return undefined;
  }

  // Mobile-first → smaller breakpoints inherit from smaller breakpoints.
  // Desktop-first → smaller breakpoints inherit from larger breakpoints.
  // Without this distinction, edits at one end of the spectrum never
  // propagated to the other.
  static #getBroaderBreakpoints(currentBreakpoint, allBreakpoints) {
    const currentValue = parseInt(currentBreakpoint.value);
    const designVariant = this.#getDesignVariant();
    return allBreakpoints
        .map(breakpoint => ({ ...breakpoint, numericValue: parseInt(breakpoint.value) }))
        .filter(breakpoint => {
          if (designVariant !== "mobile") {
            return breakpoint.numericValue > currentValue;
          }
          return breakpoint.numericValue < currentValue;
        })
        // Nearest neighbour first: desktop-first ascends from the smallest
        // wider; mobile-first descends from the largest narrower.
        .sort((a, b) => {
          return designVariant !== "mobile"
            ? a.numericValue - b.numericValue
            : b.numericValue - a.numericValue;
        });
  }

  static #getDesignVariant() {
    /* eslint-disable global-require */
    const domStateModule = require("%/dynamic-component-editor/stores/dynamic-component-dom-state-store");
    /* eslint-enable global-require */
    const useDomStateStore = domStateModule.useDynamicComponentDomStateStore ?? domStateModule.default;
    // Canvas's destination-aware designVariant (source -> config -> desktop) so
    // the cascade direction follows the destination's design mode.
    return useDomStateStore?.()?.designVariant ?? "desktop";
  }

  // Walk the destination's BrandingElement chain. Returns the leaf the
  // chain produces, or undefined when no BrandingElement matches (caller
  // falls through to OptionClass default).
  static #resolveLeafFromBranding(elementData, optionKey, context, breakpoint = "default", state = "default") {
    if (!elementData?.type) {
      return undefined;
    }

    const themeElementResolver = this.#getThemeElementResolver(elementData, context);
    if (!themeElementResolver) {
      return undefined;
    }

    /* eslint-disable global-require */
    const DynamicElementModule = require("%/dynamic-component-editor/classes/dynamic-element");
    /* eslint-enable global-require */
    const DynamicElementClass = DynamicElementModule.default;

    // Pass the wrapping DC + variable overrides so the chain walk's
    // `determineVariant` / `determineSize` probe sees the live value of
    // any variable-bound branding-determining option (button-type,
    // button-size, styled-text-variant). Without this, an embedded
    // element keeps its persisted variant for the chain lookup even
    // when the host DC's variable has been switched, so the iButton
    // variant CSS class flips but variant-specific styles (background
    // colour, padding, …) stay pinned to the original variant.
    const brandingTarget = DynamicElementClass.getBrandingTargetForElementType(
      elementData.type,
      elementData,
      context?.dynamicComponent ?? null,
      context?.variableOverrides ?? null,
      breakpoint,
      state,
      context,
    );
    if (!brandingTarget) {
      return undefined;
    }

    // Theme-element self-reads start the chain at the entity's own
    // brandedVariant/brandedSize. Theme rows don't carry routing keys
    // (styled-text-variant, button-type) in their seed, so without this
    // override the probe collapses to the default coord.
    if (elementData.elementType === "theme") {
      const variant = elementData.brandedVariant ?? null;
      const size = elementData.brandedSize ?? null;
      if (variant !== null && variant !== "") {
        brandingTarget.variant = variant;
      }
      brandingTarget.size = size === "" ? null : size;
    }

    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementData.type, context);
    if (!optionClass) {
      return undefined;
    }

    return DynamicElementClass.buildBrandingDefaultLeaf(
      brandingTarget,
      optionClass,
      context.themeId,
      context.themeMode,
      themeElementResolver,
      state,
    );
  }

  // Pick the lookup that knows where this element's BrandingElements
  // live. Canvas elements use the owning DC's per-coord index; theme
  // elements have no DC, so fall back to BaseBranding's destination
  // index.
  static #getThemeElementResolver(elementData, context) {
    const dynamicComponent = context?.dynamicComponent;
    if (dynamicComponent && typeof dynamicComponent.getThemeElementFor === "function") {
      return dynamicComponent;
    }
    if (elementData.elementType !== "theme") {
      return null;
    }
    const destinationId = elementData.destinationId ?? null;
    if (destinationId === null || destinationId === undefined || destinationId === "") {
      return null;
    }
    return (type, variant, size) => {
      return BaseBranding.findThemeElementFor(destinationId, type, variant, size);
    };
  }

  // Final fallback: materialise the OptionClass static default.
  static #buildOptionClassDefaultLeaf(elementData, optionKey, context) {
    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementData.type, context);
    return this.buildClassDefaultValue(optionClass, context.themeId, context.themeMode);
  }

  /** ====== Private — write helpers ====== */

  // Per-leaf seed dispatcher used by processElementOptions. Mirrors the
  // legacy logic: when the element has a linked variable for this option,
  // skip the seed at custom breakpoint/state slots (the variable is the
  // only authoritative value); otherwise, ask processElementOption for
  // the default leaf at this coord.
  static #processSeedLeaf(optionKey, breakpoint, state, elementData, variableOverrides, themeMode, context) {
    const fullKey = this.#buildOptionFullKey(optionKey, state, breakpoint);
    let rawValue;

    if (!variableOverrides[optionKey]) {
      rawValue = elementData.options?.[fullKey];
    } else if (!this.#isCustomState(state) && !this.#isCustomBreakpoint(breakpoint)) {
      rawValue = variableOverrides[optionKey];
    } else {
      return undefined;
    }

    return this.processElementOption(elementData, optionKey, breakpoint, state, rawValue, themeMode, context);
  }

  static #getApplicableVariableOverwrites(elementTemplateId, optionVariables, linkedOptionVariables) {
    return Object.keys(linkedOptionVariables).filter(variableKey => {
      return variableKey.startsWith(elementTemplateId);
    }).reduce((overrides, variableKey) => {
      const optionKey = variableKey.split(".").at(-1);
      const relatedVariable = optionVariables[linkedOptionVariables[variableKey].linkedToVariable];
      if (relatedVariable?.value) {
        overrides[optionKey] = relatedVariable.value;
      }
      return overrides;
    }, {});
  }

  static #rawIsThemeReference(rawValue) {
    return rawValue !== null
      && typeof rawValue === "object"
      && typeof rawValue.source === "string"
      && rawValue.source.startsWith("theme.");
  }

  // True when the OptionClass's compile fell through and just returned
  // the raw theme-ref's `value` key as the compiled output. Several
  // option classes implement `convertConfigValueToCompiledValue` as a
  // theme lookup with passthrough fallback — when the theme doesn't
  // know the key, the function returns the bare key string. Treat
  // that as compile failure so consumers get a valid fallback instead
  // of an unrenderable theme key.
  static #compileIsThemeRefPassthrough(rawValue, compiledValue) {
    if (!this.#rawIsThemeReference(rawValue)) {
      return false;
    }
    return compiledValue === rawValue.value;
  }

  // True for option keys owned by the element's branding provider
  // (e.g. font-color on a BrandedHeadingElement, padding on a Button).
  // These default to bare `{i: true}` so the chain is the source of truth.
  static #optionInheritsFromBranding(elementData, optionKey) {
    if (!this.#elementHasBrandingProvider(elementData)) {
      return false;
    }
    if (elementData.elementType === "theme") {
      return false;
    }
    const elementDef = DynamicElement.getElementDefinition(elementData.type);
    const BrandingClass = elementDef?.brandingType ?? null;
    if (!BrandingClass || typeof BrandingClass.isThemableOptionKey !== "function") {
      return false;
    }
    try {
      return BrandingClass.isThemableOptionKey(optionKey);
    } catch {
      return false;
    }
  }

  static #elementHasBrandingProvider(elementData) {
    if (!elementData?.type) {
      return false;
    }
    return Boolean(DynamicElement.getElementDefinition(elementData.type)?.brandingType);
  }

  // The keys the branding probe READS to pick which BrandingElement to
  // walk. Resolving these MUST NOT re-enter the chain (probe → resolver
  // → probe = infinite loop). They terminate at the OptionClass default
  // — which IS the canonical default for variant/size selectors.
  // `text-style-option` and `text-branding-style-option` are consumed
  // by `TypographyBranding.determineType` to pick the chain row
  // (BrandedHeadingElement vs BrandedParagraphElement vs …), so they
  // must short-circuit here too.
  static #optionIsBrandingDeterminingKey(optionKey) {
    return optionKey === "button-type"
      || optionKey === "button-size"
      || optionKey === "styled-text-variant"
      || optionKey === "text-style-option"
      || optionKey === "text-branding-style-option";
  }

  // `convertConfigValueToCompiledValue` is a STATIC method on every
  // option class. The class instances we hold (from
  // `getElementOptionClasses` / `getElementOptionBundles`) don't expose
  // statics directly — calling `optionClass.convertConfigValueToCompiledValue`
  // throws "is not a function". Always go through `.constructor` so the
  // call lands on the class.
  static #tryCompile(optionClass, rawValue, themeId, themeMode, where = {}) {
    if (!optionClass) {
      return undefined;
    }
    const Klass = typeof optionClass === "function" ? optionClass : optionClass.constructor;
    if (!Klass || typeof Klass.convertConfigValueToCompiledValue !== "function") {
      return undefined;
    }
    try {
      return Klass.convertConfigValueToCompiledValue(rawValue, themeId, themeMode);
    } catch (error) {
      // Swallowed compile throw leaks the RAW value downstream — name it, don't hide it.
      reportOptionCompileFailure({ className: Klass?.name, rawValue, themeId, themeMode, error, ...where });
      return undefined;
    }
  }

  static #getRawDefaultValue(breakpoint, state, optionKey, elementData, context) {
    const brandingValue = this.#getRawBrandingValue(breakpoint, state, optionKey, elementData, context, true);
    if (this.#isCustomState(state) || this.#isCustomBreakpoint(breakpoint) || brandingValue !== undefined) {
      return undefined;
    }
    const classDefault = this.#getOptionClassDefault(optionKey, "default", elementData.type, context);
    if (classDefault === undefined) {
      throw new Error(
        `No default value found for option on default breakpoint: ${optionKey}, state: ${state}, elementType: ${elementData.type}`,
      );
    }
    return classDefault;
  }

  static #getOptionClassDefault(optionKey, state, elementType, context) {
    const optionClass = this.#getOptionClassByOptionKey(optionKey, elementType, context);
    if (!optionClass) {
      return undefined;
    }
    const defaultValues = optionClass.getValueOrDefault({});
    return defaultValues[`${state}.${optionKey}`];
  }

  static #getRawBrandingValue(breakpoint, state, optionKey, elementData, context, mergeWithConfig = false) {
    const elementDef = DynamicElement.getElementDefinition(elementData.type);
    const BrandingClass = elementDef?.brandingType;
    if (!BrandingClass || optionKey === "text-option") {
      return undefined;
    }
    const brandingInstance = new BrandingClass();
    const { type, variant, size } = this.#getThemeMetaForElement(
      { ...elementData, elementDef }, breakpoint, state, context,
    );
    return brandingInstance.getRawValue(
      optionKey, breakpoint, state, type, variant, size, context.breakpoints, mergeWithConfig ? elementData.options : {},
    );
  }

  static #getThemeMetaForElement(elementData, breakpoint, state, context) {
    if (elementData.elementType === "theme") {
      return { type: elementData.brandedType, variant: elementData.brandedVariant, size: elementData.brandedSize };
    }
    const BrandingClass = DynamicElement.getElementDefinition(elementData.type)?.brandingType;
    if (BrandingClass) {
      const brandingInstance = new BrandingClass();
      return {
        type: brandingInstance.determineType(elementData, breakpoint, state, context.breakpoints),
        variant: brandingInstance.determineVariant(elementData, breakpoint, state, context.breakpoints),
        size: brandingInstance.determineSize(elementData, breakpoint, state, context.breakpoints),
      };
    }
    return { type: null, variant: null, size: null };
  }

  /** ====== Private — persistence helpers ====== */

  // Walk the persisted tree and clean stale caches:
  //   theme-bound leaf with `c` → drop `c` (cross-session staleness risk;
  //                               cv stamps are gone, the resolver
  //                               re-derives `c` against the live theme).
  //   `{i: true}` with `r/c` cache → drop the snapshot, leave `{i: true}`
  //                                  so the resolver re-walks the chain.
  static #cleanPersistedTree(tree) {
    if (!tree || typeof tree !== "object") {
      return tree;
    }
    for (const mode of Object.keys(tree)) {
      const breakpoints = tree[mode];
      if (!breakpoints || typeof breakpoints !== "object") {
        continue;
      }
      for (const breakpoint of Object.keys(breakpoints)) {
        const states = breakpoints[breakpoint];
        if (!states || typeof states !== "object") {
          continue;
        }
        for (const state of Object.keys(states)) {
          const optionsAtCoord = states[state];
          if (!optionsAtCoord || typeof optionsAtCoord !== "object") {
            continue;
          }
          for (const optionKey of Object.keys(optionsAtCoord)) {
            optionsAtCoord[optionKey] = this.#cleanPersistedLeaf(optionsAtCoord[optionKey]);
          }
        }
      }
    }
    return tree;
  }

  static #cleanPersistedLeaf(leaf) {
    if (!leaf || typeof leaf !== "object") {
      return leaf;
    }
    // Inherited leaves are NEVER user-authored — the `r` was snapshotted
    // from whatever the branding chain returned on the last save, so
    // re-using it here would lock the value in even after the user
    // changes the routing key (`text-branding-style-option` /
    // `styled-text-variant` / `button-variant`). A UrlElement that
    // started as "paragraph" persists `r: theme.font.standard`; flipping
    // its style to "title" should re-walk BrandedTitleElement.standard's
    // `font-color`, but kept-`r` made every read short-circuit on the
    // stale snapshot and the colour never changed. Reduce the leaf to a
    // pure `{i: true}` here — the order matters because `#leafIsThemeBound`
    // also matched on `r`'s `{source: "theme.*", value: ...}` shape and
    // would otherwise keep the snapshot.
    if (leaf.i === true) {
      return { i: true };
    }
    if (this.#leafIsThemeBound(leaf)) {
      const cleaned = { ...leaf };
      delete cleaned.c;
      delete cleaned.cv;
      return cleaned;
    }
    if ("cv" in leaf) {
      const cleaned = { ...leaf };
      delete cleaned.cv;
      return cleaned;
    }
    return leaf;
  }

  static #cleanModeBranch(modeTree) {
    const cleaned = {};
    for (const breakpoint of Object.keys(modeTree || {})) {
      const states = modeTree[breakpoint];
      if (!states || typeof states !== "object") {
        continue;
      }
      const cleanedBreakpoint = {};
      for (const state of Object.keys(states)) {
        const optionsAtCoord = states[state];
        if (!optionsAtCoord || typeof optionsAtCoord !== "object") {
          continue;
        }
        const cleanedState = {};
        for (const optionKey of Object.keys(optionsAtCoord)) {
          const leaf = optionsAtCoord[optionKey];
          if (leaf !== null && typeof leaf === "object" && (leaf.r !== undefined || leaf.c !== undefined)) {
            cleanedState[optionKey] = leaf;
          }
        }
        if (Object.keys(cleanedState).length > 0) {
          cleanedBreakpoint[state] = cleanedState;
        }
      }
      if (Object.keys(cleanedBreakpoint).length > 0) {
        cleaned[breakpoint] = cleanedBreakpoint;
      }
    }
    return cleaned;
  }

  /** ====== Private — small utilities ====== */

  static #buildOptionFullKey(optionKey, state, breakpoint) {
    return breakpoint && breakpoint !== "default"
      ? `${state}:${breakpoint}.${optionKey}`
      : `${state}.${optionKey}`;
  }

  static #getOptionClassByOptionKey(optionKey, elementType, context) {
    const all = this.getAllOptionClassesForElement(elementType, context);
    return all.find(optionClass => optionClass.optionKey === optionKey);
  }

  static #isCustomBreakpoint(breakpoint) {
    return breakpoint && breakpoint !== "default";
  }

  static #isCustomState(state) {
    return state !== "default";
  }

  static #normaliseBreakpoint(breakpoint) {
    return breakpoint || "default";
  }
}

export default DynamicOption;
